/**
 * <analytics-figure> — the one way a number is printed on an analytics surface.
 *
 * A figure that can be interrogated wears a dotted rule under its digits — the
 * footnote convention, one hairline, no icon, no chevron, no hover-only reveal.
 * It is legible at rest, so an operator knows what is interrogable before
 * touching anything, and it scales from a display figure to a compact
 * infographic row without becoming a second kind of control.
 *
 * A figure given no `node` is printed and nothing more. That is how a surface
 * with no source of drill data — every production surface today — renders the
 * same figure without offering an affordance that would open nothing.
 *
 * The press leaves as a `drill` event: the chain that answers it is the
 * mounting surface's own, and this element holds none of it.
 *
 * The word its accessible name ends on is READ, never received. Every mount is
 * inside the one app and reads the one catalog, so a word a host passed in
 * could only ever be the same word or the wrong one — and the wrong one is
 * what an English default gets a German operator's screen reader to say under
 * a German figure.
 */
import "@mhmo91/schmancy/button";
import { formatCurrency, formatNumber } from "@mhmo91/rx";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { css, html } from "lit";
import { property } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../i18n/localize.ts";

/** What the number counts, which is what decides how it is printed. */
export type FigureUnit = "count" | "money" | "seconds" | "percent";

/** How loudly the figure is printed. The rule under it is the same hairline at
 *  every size — the affordance must not read as a different control on a
 *  display figure than on a compact infographic row. */
export type FigureSize = "hero" | "stat" | "inline";

/** What a press opens: which kind of answer it wants, which figure it is
 *  about, and the slice that figure was taken under. The element only carries
 *  it back out on the event; resolving it belongs to whoever supplied it. */
export interface DrillNode {
	readonly kind: "parts" | "provenance" | "records" | "boundary";
	readonly subject: string;
	readonly scope: readonly string[];
}

/** Money is EUR on every surface that mounts this element. */
const CURRENCY = "EUR";

/** How a figure is printed. The locale is the caller's — `SchmancyElement`
 *  carries the theme's own formatting tag, and this element passes it. */
export function formatFigure(
	value: number,
	unit: FigureUnit,
	locale: string,
): string {
	if (unit === "money") return formatCurrency(value / 100, CURRENCY, locale);
	if (unit === "percent") return `${value.toFixed(1)} %`;
	if (unit === "seconds")
		return `${Math.floor(value / 60)}:${String(Math.round(value) % 60).padStart(2, "0")} min`;
	return formatNumber(Math.round(value), locale);
}

const TYPE: Readonly<
	Record<
		FigureSize,
		{
			type: "display" | "title" | "body";
			token: "xs" | "sm" | "md" | "lg" | "xl";
		}
	>
> = {
	hero: { type: "display", token: "sm" },
	stat: { type: "title", token: "lg" },
	inline: { type: "body", token: "md" },
};

@schmancyElement("analytics-figure")
export class AnalyticsFigure extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: inline-flex;
				flex-shrink: 0;
			}
		`,
	];

	/** What the figure is of — the accessible name's subject, and the caption
	 *  when `showLabel` is set. */
	@property({ type: String }) label = "";

	@property({ type: Number }) value = 0;

	@property({ type: String }) unit: FigureUnit = "count";

	@property({ type: String }) size: FigureSize = "stat";

	/** Prints the label above the figure. Off inside a row, whose own label
	 *  column already names it. */
	@property({ type: Boolean }) showLabel = false;

	/** Prints this instead of the formatted value — for a figure whose printed
	 *  form the surface owns (a currency the row already grouped, an em dash
	 *  for an undefined rate). */
	@property({ type: String }) display = "";

	/** The figure is approximate, not exact: some of the evidence behind it
	 *  could not be read. It is NOT a floor — an omission does not only
	 *  subtract, since dropping one event can split one visit into two or move
	 *  a visit into a scope it was outside — so the true figure lies either
	 *  side of this one. Applies to the FORMATTED value only — a `display`
	 *  override is a form this surface already owns whole and is left
	 *  untouched. */
	@property({ type: Boolean }) approximate = false;

	/** What this figure opens. Absent — the figure is printed and nothing more,
	 *  which is how a surface distinguishes a number with sources from one that
	 *  is only a caption. */
	@property({ attribute: false }) node: DrillNode | null = null;

	override render() {
		const printed =
			this.display.length > 0
				? this.display
				: this.approximate
					? this.t("admin.analytics.figureApproximate", {
							value: formatFigure(this.value, this.unit, this.locale),
						})
					: formatFigure(this.value, this.unit, this.locale);
		return html`
			<div class="flex flex-col gap-1 items-start">
				${when(
					this.showLabel && this.label.length > 0,
					() => html`
						<schmancy-typography type="label" token="sm" class="text-surface-onVariant">
							${this.label}
						</schmancy-typography>
					`,
				)}
				${when(
					this.node !== null,
					() => html`
						<schmancy-button
							variant="plain"
							color="neutral"
							justify="start"
							aria-label=${`${this.label}, ${printed} — ${this.t(
								"admin.analytics.drill.openSources",
							)}`}
							class="border-b-2 border-dotted border-outline hover:border-primary-default focus-visible:border-primary-default pb-0.5"
							@click=${() => {
								if (this.node === null) return;
								this.dispatchEvent(
									new CustomEvent<DrillNode>("drill", {
										detail: this.node,
										bubbles: true,
										composed: true,
									}),
								);
							}}
						>
							<schmancy-typography
								type=${TYPE[this.size].type}
								token=${TYPE[this.size].token}
								weight=${this.size === "hero" ? "normal" : "bold"}
								class="tabular-nums"
							>
								${printed}
							</schmancy-typography>
						</schmancy-button>
					`,
					() => html`
						<schmancy-typography
							type=${TYPE[this.size].type}
							token=${TYPE[this.size].token}
							weight=${this.size === "hero" ? "normal" : "bold"}
							class="tabular-nums"
						>
							${printed}
						</schmancy-typography>
					`,
				)}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"analytics-figure": AnalyticsFigure;
	}
}
